import clsx from 'clsx';
import {
  type HTMLAttributes,
  type KeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import {
  type DataTestId,
  type StylingProps,
  type WithChildren,
  _mergeProps,
} from '@dynatrace/strato-components/core';
import { Flex, Grid } from '@dynatrace/strato-components/layouts';
import { Text } from '@dynatrace/strato-components/typography';

import { useInternalTimeframeSelectorContext } from './hooks/useInternalTimeframeSelectorContext.js';
import { useTimeframeSelectorContext } from './hooks/useTimeframeSelectorContext.js';
import { useTooltipMessages } from './hooks/useTooltipMessages.js';
import type { PresetItemVariants } from './TimeframeSelectorPresets.sty.js';
import {
  timeframeSelectorPresetItemCSS,
  timeframeSelectorPresetItemContainerCSS,
  timeframeSelectorPresetItemMediaQuery,
  timeframeSelectorPresetItemNameCSS,
  timeframeSelectorPresetItemValueCSS,
  tooltipPresetItemWrapper,
} from './TimeframeSelectorPresets.sty.js';
import { _isOutsideRange } from './utils/timeframe-selection.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';
import { SelectedOverCalendarContext } from '../../forms/date-time-range-picker/contexts/SelectedOverCalendarContext.js';
import { removePrecisionFromTimeValue } from '../../forms/date-time-range-picker/helpers.js';
import { parseValue } from '../../forms/forms-core/time/timeParser.js';
import { Tooltip } from '../../overlays/tooltip/Tooltip.js';

//#region Typings
/**
 * @public
 */
export interface TimeframeSelectorPresetItemProps
  extends HTMLAttributes<HTMLLIElement>,
    DataTestId,
    StylingProps,
    WithChildren {
  /** The value of the timeframe preset. */
  value: {
    /** Start of the time frame. */
    from: string;
    /** End of the time frame. */
    to: string;
  };
}
//#endregion

/**
 * TimeframeSelector preset item shown inside the presets list of the overlay.
 * @public
 */
export const PresetItem = (
  props: TimeframeSelectorPresetItemProps & { _isSelected?: boolean },
) => {
  const {
    children,
    value,
    _isSelected: isSelected = false,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    ...remainingProps
  }: TimeframeSelectorPresetItemProps & { _isSelected?: boolean } = props;

  const [openTooltip, setOpenTooltip] = useState(false);

  const { onChange } = useTimeframeSelectorContext();
  const { min, max, precision } = useInternalTimeframeSelectorContext();

  const isDisabled = _isOutsideRange(value, min, max);

  const tooltipId = useId();
  const tooltipFocusProps = {
    onFocus: useCallback(
      () => !isDisabled && setOpenTooltip(true),
      [isDisabled],
    ),
    onBlur: useCallback(() => setOpenTooltip(false), []),
  };

  const { focusProps, focusClassName } = useFocusRing({
    color: isSelected && !isDisabled ? 'primary' : 'neutral',
    disabled: isDisabled,
  });

  let state: NonNullable<PresetItemVariants>['state'] = isSelected
    ? 'selected'
    : 'rest';
  if (isDisabled) {
    state = 'disabled';
  }

  const {
    timeframeDisplayValue,
    formattedPresetItemValue,
    tooltipFormattedTimeValueStart,
    tooltipFormattedTimeValueEnd,
    isAbsoluteTimeframe,
    isInvalid,
  } = useTooltipMessages(value, precision, children);

  const { setSelectedOverCalendar } = useContext(SelectedOverCalendarContext);

  /** Handle clicking on the timeframe selector preset item. Uses pointerdown event so that the validation on blur doesn't trigger first. */
  const handleItemClick = useCallback(() => {
    if (isDisabled) {
      return;
    }

    const { from, to } = parseValue(value);

    if (from && to) {
      // should set selectedOverCalendar when a preset is selected.
      setSelectedOverCalendar?.(false);

      onChange({
        from: removePrecisionFromTimeValue(from, precision) ?? from,
        to: removePrecisionFromTimeValue(to, precision) ?? to,
      });
    }
  }, [isDisabled, value, setSelectedOverCalendar, onChange, precision]);

  /**
   * Temporary keydown listener to provide the possibility to select items via keyboard
   * Will change as soon as we switch to the reusable listbox component.
   */
  const handleItemKeyDown = useCallback(
    (event: KeyboardEvent<HTMLLIElement>) => {
      if (isDisabled) {
        return;
      }

      if (
        event.code === 'Enter' ||
        event.code === 'NumpadEnter' ||
        event.code === 'Space'
      ) {
        handleItemClick();
      }
    },
    [handleItemClick, isDisabled],
  );

  const liRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    if (isSelected) {
      liRef.current?.scrollIntoView();
    }
    // we don't want to scroll the element into view when isSelected changes, but only on the first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isInvalid ? null : (
    <li
      ref={liRef}
      role="option"
      aria-selected={isSelected}
      aria-disabled={isDisabled}
      className={clsx([
        focusClassName,
        timeframeSelectorPresetItemCSS({
          state,
        }),
        consumerClassName,
      ])}
      onPointerDown={handleItemClick}
      onKeyDown={handleItemKeyDown}
      data-testid={dataTestId}
      style={consumerStyle}
      {..._mergeProps(remainingProps, focusProps, tooltipFocusProps)}
    >
      <Tooltip
        id={tooltipId}
        text={
          <Flex flexDirection="column">
            <Flex gap={0} flexDirection="column">
              <Text textStyle="base-emphasized">{timeframeDisplayValue}</Text>
              <Text textStyle="small">{formattedPresetItemValue}</Text>
            </Flex>
            {!isAbsoluteTimeframe && (
              <Flex gap={0} flexDirection="column">
                <Text textStyle="small">{tooltipFormattedTimeValueStart}</Text>
                <Text textStyle="small">{tooltipFormattedTimeValueEnd}</Text>
              </Flex>
            )}
          </Flex>
        }
        placement="right"
        fallbackPlacements={[
          'right-end',
          'bottom-end',
          'bottom',
          'bottom-start',
          'left-end',
          'left',
          'left-start',
        ]}
        open={openTooltip}
        onOpenChange={setOpenTooltip}
        disabled={isDisabled}
      >
        <div
          className={tooltipPresetItemWrapper({ isSelected, isDisabled })}
          aria-describedby={tooltipId}
        >
          <Grid className={timeframeSelectorPresetItemContainerCSS}>
            <Text
              as={Flex}
              className={timeframeSelectorPresetItemNameCSS}
              textStyle="base-emphasized"
            >
              {timeframeDisplayValue}
            </Text>
            <Text
              as={Flex}
              textStyle="small"
              className={clsx(
                timeframeSelectorPresetItemMediaQuery,
                timeframeSelectorPresetItemValueCSS({
                  state,
                }),
              )}
            >
              {formattedPresetItemValue}
            </Text>
          </Grid>
        </div>
      </Tooltip>
    </li>
  );
};

PresetItem.displayName = 'TimeframeSelector.PresetItem';
